import {
  IconDashboard, IconReceivable, IconPayable, IconLedger, IconSettings,
  IconProducts, IconWarehouse, IconFactory, IconCalendar,
  IconPeople, IconGrid, IconService,
} from '@/components/ui/Icon';
import { PRODUCT_NAME } from '@/shared/branding';

export type SubItem = {
  to: string;
  label: string;
  requiresRole?: string;
  // Catalog node path for RBAC gating (e.g. 'receivables.invoices').
  // When set, the SubNav hides the link if effective[node]==='hidden'
  // and the App route is wrapped with <PermissionGate node={node}>.
  node?: string;
  // Shown only while the session company has this flag (SubNav and the
  // command palette): Receivables > QuickBooks needs a QuickBooks connection.
  requiresCompanyFlag?: 'quickbooks_connected';
  // Optional THIRD-level tabs: when this sub-tab (or any child) is active, the
  // SubNav shows a second row with these children (e.g. Admin Center →
  // Customization → Custom Fields / Form Designer / …).
  children?: SubItem[];
};
export type Section = {
  key: string;
  label: string;
  icon: typeof IconDashboard;
  // fritech-icons class (our own generated webfont, web/icon-src) for the MAIN
  // top nav only. Other icon consumers (favorites, palette) keep using `icon`.
  fa: string;
  // Per-module accent color for the nav glyph (inactive tabs only — the active
  // pill renders the icon white). Full Tailwind class so the JIT sees it.
  faColor: string;
  base: string;            // landing route when section tab is clicked
  matches: string[];       // URL prefixes that count as "inside this section"
  sub: SubItem[];
  // Show this section only to users whose role set intersects this list.
  // Omit the field (or use empty array) to mean "everyone". `admin`,
  // `superadmin`, and `read_only` bypass all gates (see `isSectionVisible`
  // below).
  requiresAnyRole?: string[];
  // When true, admin/superadmin/read_only do NOT bypass requiresAnyRole for this
  // section. Used by the self-service portal ("My HR"), which should appear only
  // for users who actually hold the employee/manager role + a linked employee
  // record — not for every admin.
  strictRole?: boolean;
  // Section's catalog node for RBAC. If absent, falls back to `key`.
  node?: string;
};

export const SECTIONS: Section[] = [
  {
    key: 'home',
    faColor: 'text-violet-700',
    fa: 'ft-house',
    label: 'Home',
    icon: IconDashboard,
    base: '/',
    matches: ['/'],
    node: 'home',
    // Single self-referential item so the second-level SubNav bar renders for
    // Home like every other module (shows the "Home" pill; page actions such as
    // Customize are portaled into the bar's right slot via <PageActions>).
    sub: [{ to: '/', label: 'Home', node: 'home' }],
  },
  {
    key: 'receivables',
    faColor: 'text-emerald-700',
    fa: 'ft-invoice-dollar',
    label: 'Receivables',
    icon: IconReceivable,
    base: '/ar-invoices',
    matches: ['/ar-invoices', '/credit-notes', '/receipts', '/contra-settlements', '/contracts', '/reports/ar-aging', '/reports/statements', '/receivables', '/collections'],
    sub: [
      { to: '/ar-invoices', label: 'Invoices', node: 'receivables.invoices' },
      { to: '/ar-invoices/lines', label: 'Invoice lines', node: 'receivables.invoices' },
      { to: '/collections', label: 'Collections', node: 'receivables.invoices' },
      { to: '/receivables/quickbooks', label: 'QuickBooks', node: 'receivables.quickbooks', requiresCompanyFlag: 'quickbooks_connected' },
      { to: '/credit-notes', label: 'Credit Notes', node: 'receivables.credit_notes' },
      { to: '/receipts', label: 'Receipts', node: 'receivables.receipts' },
      { to: '/contra-settlements', label: 'Netting', node: 'receivables.contra_settlements' },
      { to: '/contracts', label: 'Contracts', node: 'receivables.contracts' },
      { to: '/reports/ar-aging', label: 'AR Aging', node: 'receivables.ar_aging' },
      { to: '/reports/statements', label: 'Statements', node: 'receivables.statements' },
      { to: '/receivables/reports', label: 'Reports', node: 'receivables.reports' },
      { to: '/receivables/settings', label: 'Settings', node: 'receivables.settings' },
    ],
  },
  {
    key: 'payables',
    faColor: 'text-rose-700',
    fa: 'ft-credit-card',
    label: 'Payables',
    icon: IconPayable,
    base: '/vendors',
    matches: ['/vendors', '/ap-invoices', '/payments', '/payables/pay-bills', '/payables/bank-files', '/purchase-orders', '/purchase-requisitions', '/payables/vendor-price-lists', '/reports/ap-aging', '/vendor-hub', '/expense-categories'],
    sub: [
      { to: '/vendors', label: 'Vendors', node: 'payables.vendors' },
      { to: '/expense-categories', label: 'Expense Categories', node: 'payables.vendors' },
      { to: '/purchase-requisitions', label: 'Requisitions', node: 'payables.purchase_requisitions' },
      { to: '/purchase-orders', label: 'Purchase Orders', node: 'payables.purchase_orders' },
      { to: '/purchase-orders/lines', label: 'PO lines', node: 'payables.purchase_orders' },
      { to: '/payables/vendor-price-lists', label: 'Vendor Price Lists', node: 'payables.vendor_price_lists' },
      { to: '/ap-invoices', label: 'Bills', node: 'payables.bills' },
      { to: '/ap-invoices/lines', label: 'Bill lines', node: 'payables.bills' },
      { to: '/payables/pay-bills', label: 'Pay Bills', node: 'payables.payments' },
      { to: '/ap-invoices/scan-inbox', label: 'Scan Inbox', node: 'payables.bills' },
      { to: '/payments', label: 'Payments', node: 'payables.payments' },
      { to: '/payables/bank-files', label: 'Bank Files', node: 'payables.bank_files' },
      { to: '/reports/ap-aging', label: 'AP Aging', node: 'payables.ap_aging' },
    ],
  },
  {
    key: 'sales',
    faColor: 'text-orange-700',
    fa: 'ft-cart',
    label: 'Sales',
    icon: IconReceivable,
    base: '/customers',
    matches: ['/customers', '/quotes', '/sales-orders', '/agents', '/forecasts', '/sales/price-lists', '/sales-catalogs', '/sales/opportunities', '/sales/leads', '/sales/trade-shows', '/sales/b2b'],
    sub: [
      { to: '/customers', label: 'Customers', node: 'sales.customers' },
      { to: '/sales/opportunities', label: 'Opportunities', node: 'sales.opportunities' },
      { to: '/sales/leads', label: 'Leads', node: 'sales.leads' },
      { to: '/sales/trade-shows', label: 'Trade Shows', node: 'sales.leads' },
      { to: '/quotes', label: 'Quotes', node: 'sales.orders' },
      { to: '/sales-orders', label: 'Orders', node: 'sales.orders' },
      { to: '/sales-orders/lines', label: 'Order lines', node: 'sales.orders' },
      { to: '/agents', label: 'Sales Reps', node: 'sales.agents' },
      { to: '/sales/price-lists', label: 'Price Lists', node: 'sales.price_lists' },
      // Edited under the items node: the server gates the catalogs the same way.
      { to: '/sales-catalogs', label: 'Sales catalogs', node: 'products.items' },
      { to: '/forecasts', label: 'Forecasts', node: 'sales.forecasts' },
      { to: '/sales/b2b', label: 'B2B Portal', node: 'sales.b2b', children: [
        { to: '/sales/b2b', label: 'Dashboard', node: 'sales.b2b.orders' },
        { to: '/sales/b2b/orders', label: 'Orders', node: 'sales.b2b.orders' },
        { to: '/sales/b2b/customers', label: 'Customers', node: 'sales.b2b.admin' },
        { to: '/sales/b2b/users', label: 'Portal Users', node: 'sales.b2b.admin' },
        { to: '/sales/b2b/products', label: 'Products', node: 'sales.b2b.admin' },
        { to: '/sales/b2b/inventory', label: 'Inventory', node: 'sales.b2b.orders' },
        { to: '/sales/b2b/settings', label: 'Portal Settings', node: 'sales.b2b.admin' },
        { to: '/sales/b2b/audit', label: 'Audit Log', node: 'sales.b2b.admin' },
      ] },
    ],
  },
  {
    key: 'projects',
    faColor: 'text-sky-700',
    fa: 'ft-clipboard-check',
    label: 'Projects',
    icon: IconCalendar,
    base: '/projects',
    matches: ['/projects', '/time-entries', '/rfis', '/submittals'],
    node: 'projects',
    sub: [
      { to: '/projects', label: 'Projects', node: 'projects.projects' },
      { to: '/time-entries', label: 'Time Tracking', node: 'projects.time' },
      { to: '/cost-codes', label: 'Cost codes', node: 'projects.cost_codes' },
      { to: '/rfis', label: 'RFIs', node: 'projects.rfis' },
      { to: '/submittals', label: 'Submittals', node: 'projects.submittals' },
    ],
  },
  {
    key: 'hr',
    faColor: 'text-teal-700',
    fa: 'ft-users',
    label: 'HR',
    icon: IconPeople,
    base: '/hr/employees',
    matches: ['/hr'],
    sub: [
      { to: '/hr/employees', label: 'Employees', node: 'hr.employees' },
      { to: '/hr/positions', label: 'Positions', node: 'hr.positions' },
      { to: '/hr/payroll/runs', label: 'Pay Runs', node: 'hr.payroll' },
      { to: '/hr/payroll/config', label: 'Payroll Config', node: 'hr.payroll.config' },
      { to: '/hr/payroll/forms', label: 'Year-End Forms', node: 'hr.payroll.forms' },
      { to: '/hr/benefits/plans', label: 'Benefit Plans', node: 'hr.benefits' },
      { to: '/hr/benefits/enrollments', label: 'Enrollments', node: 'hr.benefits' },
      { to: '/hr/benefits/compliance', label: 'Benefits Compliance', node: 'hr.benefits.compliance' },
    ],
  },
  {
    // HR self-service portal (Wave C). Visible only to users holding the
    // `employee` or `manager` role (admins/superadmin/read_only see it via the
    // wildcard). Each sub-item is gated by a portal.* node, which is granted to
    // those roles server-side; the manager-only Team tab carries portal.team.
    key: 'portal',
    faColor: 'text-cyan-700',
    fa: 'ft-id-card',
    label: 'My HR',
    icon: IconPeople,
    base: '/portal/profile',
    matches: ['/portal'],
    requiresAnyRole: ['employee', 'manager'],
    strictRole: true, // admins without an employee link should NOT see "My HR"
    node: 'portal',
    sub: [
      { to: '/portal/profile', label: 'Profile', node: 'portal.profile' },
      { to: '/portal/pay', label: 'Pay', node: 'portal.pay' },
      { to: '/portal/benefits', label: 'Benefits', node: 'portal.benefits' },
      { to: '/portal/team', label: 'Team', node: 'portal.team' },
    ],
  },
  {
    key: 'products',
    faColor: 'text-amber-700',
    fa: 'ft-cube',
    label: 'Products',
    icon: IconProducts,
    base: '/items',
    matches: ['/items', '/item-families', '/uoms', '/uom-conversions'],
    sub: [
      { to: '/items', label: 'Items', node: 'products.items' },
      { to: '/item-families', label: 'Categories', node: 'products.families' },
      { to: '/uoms', label: 'Units of Measure', node: 'products.uoms' },
      { to: '/uom-conversions', label: 'Conversions', node: 'products.uom_conversions' },
    ],
  },
  {
    key: 'production',
    faColor: 'text-indigo-700',
    fa: 'ft-industry',
    label: 'Production',
    icon: IconFactory,
    base: '/manufacturing-orders',
    matches: ['/mrp', '/manufacturing-orders', '/work-centers', '/work-center-groups', '/routings', '/boms', '/capacity-plan', '/production-schedule', '/production-gantt', '/production-stats', '/production/settings'],
    sub: [
      { to: '/boms', label: 'BOMs', node: 'production.settings.boms' },
      { to: '/manufacturing-orders', label: 'Manufacturing Orders', node: 'production.manufacturing_orders' },
      { to: '/manufacturing-orders/report-production', label: 'Report Production', node: 'production.report_production' },
      { to: '/production-schedule', label: 'Schedule', node: 'production.schedule' },
      { to: '/production-gantt', label: 'Gantt', node: 'production.gantt' },
      { to: '/capacity-plan', label: 'Capacity Plan', node: 'production.capacity_plan' },
      { to: '/mrp/runs', label: 'MRP Runs', node: 'production.mrp_runs' },
      { to: '/production-stats', label: 'Statistics', node: 'production.statistics' },
      // Master Data page bundles the configuration entities (BOMs,
      // Routings, Work Centers, Work Center Groups) behind an in-page
      // tab strip — same pattern as /ledger/settings.
      { to: '/production/settings', label: 'Master Data', node: 'production.settings' },
    ],
  },
  {
    key: 'inventory',
    faColor: 'text-lime-800',
    fa: 'ft-warehouse',
    label: 'Inventory',
    icon: IconWarehouse,
    base: '/inventory/on-hand',
    matches: ['/inventory', '/warehouses', '/shipments', '/packs', '/inventory-counts'],
    sub: [
      { to: '/inventory/on-hand', label: 'On Hand', node: 'inventory.on_hand' },
      { to: '/inventory/transactions', label: 'Transactions', node: 'inventory.transactions' },
      { to: '/inventory/movements', label: 'Movement log', node: 'inventory.transactions' },
      // Warehouse Transfers is no longer a menu entry (owner 2026-10-18): moves between warehouses are made
      // as a Transfer in Inventory > Transactions. The /inventory/transfers pages stay reachable by link.
      { to: '/inventory-counts', label: 'Inventory counts', node: 'inventory.counts' },
      { to: '/shipments', label: 'Customer Shipments', node: 'inventory.shipments' },
      { to: '/packs', label: 'Packs', node: 'inventory.packs' },
      { to: '/inventory/lots', label: 'Lots', node: 'inventory.lots' },
      { to: '/inventory/serials', label: 'Serials', node: 'inventory.serials' },
      { to: '/inventory/statuses', label: 'Statuses', node: 'inventory.statuses' },
      { to: '/warehouses', label: 'Warehouses', node: 'inventory.warehouses' },
    ],
  },
  {
    key: 'ledger',
    faColor: 'text-blue-800',
    fa: 'ft-book',
    label: 'Ledger',
    icon: IconLedger,
    base: '/accounts',
    matches: [
      '/accounts', '/journal', '/ledger',
      '/bank-accounts', '/banking', '/bank-statements', '/bank-reconciliations',
      '/reports/trial-balance', '/reports/profit-loss', '/reports/balance-sheet',
      '/reports/tax-summary', '/reports/control-reconciliation', '/reports/inventory-valuation',
    ],
    sub: [
      { to: '/accounts', label: 'Chart of Accounts', node: 'ledger.accounts' },
      { to: '/journal', label: 'Journal Entries', node: 'ledger.journal' },
      { to: '/journal/recurring', label: 'Recurring', node: 'ledger.journal' },
      // One Banking section: the account MASTER records and the two process
      // views ride as third-level tabs. Parent lands on Accounts.
      { to: '/bank-accounts', label: 'Banking', node: 'ledger.bank_accounts', children: [
        { to: '/bank-accounts', label: 'Accounts', node: 'ledger.bank_accounts' },
        { to: '/banking', label: 'Statements', node: 'ledger.bank_accounts' },
        { to: '/banking/reconciliations', label: 'Reconciliations', node: 'ledger.bank_accounts' },
      ] },
      // All financial statements/reports under ONE pill — each report is a
      // third-level tab. Parent lands on the first child (P&L).
      { to: '/reports/profit-loss', label: 'Financial Reports', node: 'ledger.profit_loss', children: [
        { to: '/reports/profit-loss', label: 'Profit & Loss', node: 'ledger.profit_loss' },
        { to: '/reports/balance-sheet', label: 'Balance Sheet', node: 'ledger.balance_sheet' },
        { to: '/reports/cash-flow', label: 'Cash Flows', node: 'ledger.balance_sheet' },
        { to: '/reports/trial-balance', label: 'Trial Balance', node: 'ledger.trial_balance' },
        { to: '/reports/control-reconciliation', label: 'Control Reconciliation', node: 'ledger.trial_balance' },
        { to: '/reports/inventory-valuation', label: 'Inventory Valuation', node: 'ledger.trial_balance' },
        { to: '/reports/tax-summary', label: 'Tax Summary', node: 'ledger.tax_codes' },
        { to: '/reports/fx-exposure', label: 'FX Exposure', node: 'ledger.balance_sheet' },
        { to: '/reports/ledger-audit', label: 'Audit Trail', node: 'ledger' },
      ] },
      { to: '/ledger/settings', label: 'Settings', node: 'ledger.settings' },
    ],
  },
  {
    key: 'wms',
    faColor: 'text-neutral-800',
    fa: 'ft-barcode',
    label: 'WMS',
    icon: IconGrid,
    base: '/wms/waves',
    matches: ['/wms'],
    sub: [
      { to: '/wms/asns', label: 'ASN', node: 'wms.asn' },
      { to: '/wms/dock', label: 'Dock Schedule', node: 'wms.dock' },
      { to: '/wms/goods-receipts', label: 'Goods Receipts', node: 'wms.grn' },
      { to: '/wms/license-plates', label: 'License Plates', node: 'wms.lp' },
      // wms.qc and wms.putaway are RF-only flows (no web detail page); RBAC nodes
      // 'wms.qc' / 'wms.putaway' gate the RF routes in permission-catalog.js.
      { to: '/wms/bins', label: 'Bins', node: 'wms.bins' },
      { to: '/wms/zones', label: 'Zones', node: 'wms.bins' },
      { to: '/wms/slotting', label: 'Slotting', node: 'wms.bins' },
      { to: '/wms/cycle-count', label: 'Cycle Count', node: 'wms.tasks' },
      { to: '/wms/waves', label: 'Waves', node: 'wms.waves' },
      { to: '/wms/outbound', label: 'Outbound', node: 'wms.waves' },
      { to: '/wms/tasks', label: 'Tasks', node: 'wms.tasks' },
    ],
  },
  {
    key: 'service',
    faColor: 'text-red-700',
    fa: 'ft-wrench',
    label: 'Service',
    icon: IconService,
    base: '/service/jobs',
    matches: ['/service'],
    node: 'service',
    sub: [
      { to: '/service/calls', label: 'Calls', node: 'service.calls' },
      { to: '/service/jobs', label: 'Service Jobs', node: 'service.jobs' },
      { to: '/service/dispatch', label: 'Dispatch', node: 'service.jobs' },
      { to: '/service/assets', label: 'Assets', node: 'service.assets' },
      { to: '/service/rmas', label: 'RMAs', node: 'service.rmas' },
      { to: '/service/contracts', label: 'Contracts', node: 'service.contracts' },
    ],
  },
  {
    key: 'bi',
    faColor: 'text-fuchsia-800',
    fa: 'ft-chart-column',
    label: 'Analytics',
    icon: IconDashboard,
    base: '/bi',
    matches: ['/bi', '/cashflow', '/financial-overview'],
    node: 'bi',
    sub: [
      { to: '/bi/summary', label: 'Summary', node: 'ledger.profit_loss' },
      { to: '/bi/customers', label: 'Customers', node: 'sales.customers' },
      { to: '/bi/growth', label: 'Growth', node: 'ledger.profit_loss' },
      { to: '/bi/yoy', label: 'YoY', node: 'ledger.profit_loss' },
      { to: '/bi/products', label: 'Products', node: 'products.items' },
      { to: '/bi/trend', label: 'Trend', node: 'ledger.profit_loss' },
      { to: '/bi/sales', label: 'Sales Orders', node: 'sales.orders' },
      { to: '/bi/purchasing', label: 'Purchase Orders', node: 'payables.purchase_orders' },
      { to: '/bi/pnl', label: 'Profit & Loss', node: 'ledger.profit_loss' },
      { to: '/bi/balancesheet', label: 'Balance Sheet', node: 'ledger.balance_sheet' },
      { to: '/cashflow', label: 'Cash Flow', node: 'ledger.balance_sheet' },
      { to: '/bi/reports', label: 'Reports', node: 'ledger.trial_balance' },
      { to: '/bi/insights', label: 'AI Insights', node: 'ledger.profit_loss' },
      { to: '/bi/projects', label: 'Projects', node: 'projects.projects' },
    ],
  },
  {
    key: 'admin',
    faColor: 'text-slate-600',
    fa: 'ft-gear',
    label: 'Admin Center',
    icon: IconSettings,
    requiresAnyRole: ['admin'],
    // Land on Current Company — every admin sees it. Manage Companies is
    // reachable from the sub-nav for superadmins only.
    base: '/admin/company',
    matches: ['/admin', '/coming-soon', '/settings/email', '/settings/email-templates'],
    sub: [
      { to: '/admin/company', label: 'Current Company', node: 'admin.company' },
      { to: '/admin/users', label: 'Users', node: 'admin.users' },
      // Master Data groups the foundational org/document-config records under
      // one nested tab (renders as a third-level row, same as Customization).
      // Parent lands on its first child; children ride under it (not node-
      // filtered individually — consistent with the Customization group).
      { to: '/admin/branches', label: 'Master Data', node: 'admin.company', children: [
        { to: '/admin/branches', label: 'Branches', node: 'admin.branches' },
        { to: '/admin/departments', label: 'Departments', node: 'admin.departments' },
        { to: '/admin/document-numbering', label: 'Document Numbering', node: 'admin.document_numbering' },
        { to: '/admin/document-types', label: 'Document Types', node: 'admin.company' },
        { to: '/admin/document-templates', label: 'Document Templates', node: 'admin.document_templates' },
      ] },
      { to: '/admin/roles', label: 'Roles & Permissions', node: 'admin.roles' },
      { to: '/admin/audit', label: 'Security Audit', node: 'admin.roles' },
      { to: '/admin/webhooks', label: 'Webhooks', node: 'admin.webhooks' },
      { to: '/admin/integrations', label: 'Integrations', node: 'admin.integrations' },
      { to: '/admin/customization', label: 'Customization', node: 'admin.customization', children: [
        { to: '/admin/customization', label: 'Overview', node: 'admin.customization' },
        { to: '/admin/custom-fields', label: 'Custom Fields', node: 'admin.customization' },
        { to: '/admin/form-designer', label: 'Form Designer', node: 'admin.customization' },
        { to: '/admin/custom-objects', label: 'Custom Objects', node: 'admin.customization' },
        { to: '/admin/menus', label: 'Menus', node: 'admin.customization' },
        { to: '/admin/report-builder', label: 'Report Builder', node: 'admin.customization' },
        { to: '/admin/dashboards', label: 'Dashboards', node: 'admin.customization' },
        { to: '/admin/automation', label: 'Automations', node: 'admin.customization' },
        { to: '/admin/checks', label: 'Check Engine', node: 'admin.checks' },
        { to: '/admin/workflows', label: 'Workflows', node: 'admin.customization' },
        { to: '/admin/procedures', label: 'Procedures', node: 'admin.procedures' },
        { to: '/admin/governance', label: 'Governance', node: 'admin.customization' },
        { to: '/admin/api-tokens', label: 'API Tokens', node: 'admin.customization' },
      ] },
      { to: '/settings/email', label: 'Email', node: 'admin.company' },
      { to: '/settings/email-templates', label: 'Email templates', node: 'admin.company' },
      { to: '/admin/new-company', label: 'Manage Companies', requiresRole: 'superadmin', node: 'admin.new_company' },
    ],
  },
];

// Role → section visibility. The three "escape hatches" —
//   - admin     (tenant administrator; sees all modules to do their job)
//   - superadmin (reserved email; cross-tenant powers)
//   - read_only (explicit "can see everything, change nothing")
// — bypass every requiresAnyRole gate. Otherwise the user needs at least
// one role listed on the section.
const ROLE_WILDCARDS = new Set(['admin', 'superadmin', 'read_only']);

export function isSectionVisible(section: Section, userRoles: string[] | undefined | null): boolean {
  const required = section.requiresAnyRole;
  if (!required || required.length === 0) return true;
  const roles = userRoles || [];
  // strictRole sections (e.g. the self-service portal) are NOT bypassed by the
  // admin/superadmin/read_only wildcard — they require the literal role.
  if (!section.strictRole && roles.some((r) => ROLE_WILDCARDS.has(r))) return true;
  return required.some((r) => roles.includes(r));
}

export function sectionForPath(pathname: string): Section | undefined {
  // Prefer the longest-matching prefix so `/reports/ar-aging` finds "receivables"
  // before it would accidentally match a broader `/reports` section.
  let best: Section | undefined;
  let bestLen = -1;
  for (const s of SECTIONS) {
    for (const m of s.matches) {
      if (m === '/' && pathname === '/') {
        if (bestLen < 1) { best = s; bestLen = 1; }
      } else if (m !== '/' && (pathname === m || pathname.startsWith(m + '/'))) {
        if (m.length > bestLen) { best = s; bestLen = m.length; }
      }
    }
  }
  return best;
}

const routeMatches = (pathname: string, to: string) => pathname === to || pathname.startsWith(to + '/');

// The second-level tab a path belongs to: the LONGEST matching sub route, so
// nested pages label as themselves (/manufacturing-orders/report-production ->
// "Report Production", not its prefix-matching parent).
export function subForPath(pathname: string, section: Section | undefined = sectionForPath(pathname)): SubItem | undefined {
  return (section?.sub ?? [])
    .filter((it) => routeMatches(pathname, it.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

// "Section · Subtab": the label a route favourite is saved under (lifted from
// FavoriteStar unchanged, so existing favourites keep their keys).
export function pageLabelForPath(pathname: string): string {
  const section = sectionForPath(pathname);
  return [section?.label, subForPath(pathname, section)?.label].filter(Boolean).join(' · ');
}

// The most specific tab for a path, third-level children included; a child
// wins a tie with its parent (the Overview child shares its parent's path), as
// SubNav highlights it.
export function tabForPath(pathname: string, section: Section | undefined = sectionForPath(pathname)): SubItem | undefined {
  let best: SubItem | undefined;
  let bestLen = -1;
  for (const it of section?.sub ?? []) {
    for (const ch of it.children ?? []) {
      if (routeMatches(pathname, ch.to) && ch.to.length > bestLen) { best = ch; bestLen = ch.to.length; }
    }
    if (routeMatches(pathname, it.to) && it.to.length > bestLen) { best = it; bestLen = it.to.length; }
  }
  return best;
}

// The page's h1 text: the open record, else the tab, else the section.
export function pageHeadingFor(pathname: string, currentLabel = ''): string {
  const section = sectionForPath(pathname);
  return currentLabel.trim() || tabForPath(pathname, section)?.label || section?.label || PRODUCT_NAME;
}

// document.title: "D-0000000272 · Invoices · Receivables · Fritech Ai Business
// Platform". Neighbours that repeat (Home · Home) collapse to one.
export function documentTitleFor(pathname: string, currentLabel = ''): string {
  const section = sectionForPath(pathname);
  const parts: string[] = [];
  for (const p of [currentLabel, tabForPath(pathname, section)?.label, section?.label, PRODUCT_NAME]) {
    const t = (p ?? '').trim();
    if (t && parts[parts.length - 1] !== t) parts.push(t);
  }
  return parts.join(' · ');
}
